import React from 'react';
import {
  Box,
  Button,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  Divider,
  Chip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PaymentIcon from '@mui/icons-material/Payment';
import { useTranslation } from 'react-i18next';

const MultiplePaymentMethodsSection = ({
  paymentMethods = [],
  onPaymentMethodsChange,
  totalAmount,
  disabled = false,
  paymentMethodOptions = [], // ✅ Accept payment methods as prop
}) => {
  const { t } = useTranslation();

  // Calculate total paid from all payment methods
  const totalPaid = paymentMethods.reduce((sum, pm) => sum + (parseFloat(pm.amount) || 0), 0);
  const remainingAmount = totalAmount - totalPaid;

  const handleAddPaymentMethod = () => {
    // Find first available payment method that hasn't been selected yet
    const selectedMethods = paymentMethods.map(pm => pm.method);
    const availableMethod = paymentMethodOptions.find(opt => !selectedMethods.includes(opt.value));
    const defaultMethod = availableMethod ? availableMethod.value : (paymentMethodOptions.length > 0 ? paymentMethodOptions[0].value : 'CASH');
    
    const newPayment = {
      id: Date.now(), // Unique ID for each payment
      method: defaultMethod,
      amount: remainingAmount > 0 ? remainingAmount.toFixed(2) : '0.00',
    };
    onPaymentMethodsChange([...paymentMethods, newPayment]);
  };

  const handleRemovePaymentMethod = (id) => {
    onPaymentMethodsChange(paymentMethods.filter((pm) => pm.id !== id));
  };

  const handlePaymentMethodChange = (id, field, value) => {
    onPaymentMethodsChange(
      paymentMethods.map((pm) =>
        pm.id === id ? { ...pm, [field]: value } : pm
      )
    );
  };

  const getMethodLabel = (value) => {
    return paymentMethodOptions.find((opt) => opt.value === value)?.label || value;
  };

  return (
    <Card variant="outlined" sx={{ mt: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon color="primary" />
            <Typography variant="h6" fontWeight="600">
              {t('payment_methods') || 'Payment Methods'}
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={handleAddPaymentMethod}
            disabled={disabled || remainingAmount <= 0}
            sx={{
              textTransform: 'none',
              borderRadius: '8px',
            }}
          >
            {t('add_payment') || 'Add Payment'}
          </Button>
        </Box>

        {/* Payment Summary */}
        <Box sx={{ mb: 2, p: 1.5, bgcolor: 'grey.50', borderRadius: '8px' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('total_invoice_amount') || 'Total Invoice Amount'}:
            </Typography>
            <Typography variant="body2" fontWeight="600">
              €{totalAmount.toFixed(2)}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('total_paid') || 'Total Paid'}:
            </Typography>
            <Typography variant="body2" fontWeight="600" color="success.main">
              €{totalPaid.toFixed(2)}
            </Typography>
          </Box>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2" fontWeight="600">
              {t('remaining') || 'Remaining'}:
            </Typography>
            <Typography
              variant="body2"
              fontWeight="600"
              color={remainingAmount > 0 ? 'error.main' : 'success.main'}
            >
              €{remainingAmount.toFixed(2)}
            </Typography>
          </Box>
        </Box>

        {/* Payment Methods List */}
        {paymentMethods.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 3,
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">
              {t('no_payment_methods_added') || 'No payment methods added yet. Click "Add Payment" to start.'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {paymentMethods.map((payment, index) => (
              <Box
                key={payment.id}
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  p: 2,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: '8px',
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>{t('payment_method') || 'Payment Method'}</InputLabel>
                    <Select
                      value={payment.method}
                      label={t('payment_method') || 'Payment Method'}
                      onChange={(e) => handlePaymentMethodChange(payment.id, 'method', e.target.value)}
                      disabled={disabled}
                    >
                      {paymentMethodOptions
                        .filter((option) => {
                          // Show the current selected method and methods not selected by others
                          return (
                            option.value === payment.method ||
                            !paymentMethods.some((pm) => pm.id !== payment.id && pm.method === option.value)
                          );
                        })
                        .map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>

                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    label={t('amount') || 'Amount'}
                    value={payment.amount}
                    onChange={(e) => handlePaymentMethodChange(payment.id, 'amount', e.target.value)}
                    disabled={disabled}
                    InputProps={{
                      startAdornment: <Box sx={{ mr: 1 }}>€</Box>,
                    }}
                    inputProps={{
                      step: '0.01',
                      min: '0',
                    }}
                  />
                </Box>

                <IconButton
                  onClick={() => handleRemovePaymentMethod(payment.id)}
                  disabled={disabled}
                  color="error"
                  size="small"
                  sx={{ mt: 0.5 }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            ))}
          </Box>
        )}

        {/* Warning if overpaid */}
        {remainingAmount < 0 && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              bgcolor: 'warning.lighter',
              borderRadius: '8px',
              border: '1px solid',
              borderColor: 'warning.main',
            }}
          >
            <Typography variant="body2" color="warning.dark">
              ⚠️ {t('overpayment_warning') || 'Warning: Total paid exceeds invoice amount!'}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default MultiplePaymentMethodsSection;
